/**
 * Custom tags API (`/crm/api/candidates/:id/custom-tags/`).
 * Candidate detail shape (including `custom_tags`) is documented in
 * `src/types/crm/candidateDetails.js`.
 *
 * Response body for `GET` and `PUT` `/crm/api/candidates/:id/custom-tags/`.
 *
 * @typedef {Object} CandidateCustomTagsPayload
 * @property {string[]} tags Ordered list of tag labels for the authenticated user.
 */

/**
 * Turn a DRF-style JSON error body into human-readable lines for the UI.
 *
 * @param {unknown} responseData Parsed `response.data` from axios.
 * @returns {string[]}
 */
export function formatCandidateCustomTagsSaveError(responseData) {
  if (!responseData || typeof responseData !== "object") {
    return ["Could not save tags. Please try again."]
  }
  /** @type {string[]} */
  const lines = []
  const pushField = (v) => {
    if (Array.isArray(v)) {
      v.forEach((x) => {
        if (x != null && x !== "") lines.push(String(x))
      })
    } else if (typeof v === "string" && v) {
      lines.push(v)
    }
  }
  pushField(responseData.tags)
  pushField(responseData.non_field_errors)
  if (responseData.detail != null) {
    const d = responseData.detail
    if (typeof d === "string") lines.push(d)
    else lines.push(JSON.stringify(d))
  }
  if (lines.length === 0) {
    lines.push("Could not save tags. Please check your entries and try again.")
  }
  return lines
}

/**
 * Map an axios error from `putCandidateCustomTags` to user-visible lines.
 *
 * @param {import("axios").AxiosError} error
 * @returns {string[]}
 */
export function formatCustomTagsSaveErrorFromAxios(error) {
  const status = error.response?.status
  const data = error.response?.data
  if (status === 400 && data && typeof data === "object") {
    return formatCandidateCustomTagsSaveError(data)
  }
  if (status === 404) {
    return ["This candidate is not available or you no longer have access."]
  }
  if (status === 401 || status === 403) {
    return ["Your session may have expired. Sign in again and retry."]
  }
  if (!error.response) {
    return ["Network error. Check your connection and try again."]
  }
  if (data && typeof data === "object") {
    return formatCandidateCustomTagsSaveError(data)
  }
  return [error.message || "Could not save tags."]
}

/**
 * @param {string|number} candidateId
 * @returns {string}
 */
export function candidateCustomTagsUrl(candidateId) {
  return `/crm/api/candidates/${candidateId}/custom-tags/`
}

export function candidateCustomTagSuggestionsUrl() {
  return "/crm/api/custom-tags/suggestions/"
}

/**
 * @param {import("axios").AxiosInstance} axios
 * @returns {Promise<import("axios").AxiosResponse<CandidateCustomTagsPayload>>}
 */
export function getCandidateCustomTagSuggestions(axios) {
  return axios.get(candidateCustomTagSuggestionsUrl())
}

/**
 * @param {import("axios").AxiosInstance} axios
 * @param {string|number} candidateId
 * @returns {Promise<import("axios").AxiosResponse<CandidateCustomTagsPayload>>}
 */
export function getCandidateCustomTags(axios, candidateId) {
  return axios.get(candidateCustomTagsUrl(candidateId))
}

/**
 * Replace all custom tags for this candidate and the current user (full list).
 *
 * @param {import("axios").AxiosInstance} axios
 * @param {string|number} candidateId
 * @param {string[]} tags
 * @returns {Promise<import("axios").AxiosResponse<CandidateCustomTagsPayload>>}
 */
export function putCandidateCustomTags(axios, candidateId, tags) {
  return axios.put(candidateCustomTagsUrl(candidateId), { tags })
}
